/**
 * Environment-aware logger utility
 * Only logs in development mode to keep production clean
 */

const isDevelopment = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  prefix?: string;
  forceLog?: boolean; // For critical errors that should always log
}

class Logger {
  private prefix: string;

  constructor(prefix?: string) {
    this.prefix = prefix ? `[${prefix}]` : '';
  }

  private formatMessage(message: string): string {
    return this.prefix ? `${this.prefix} ${message}` : message;
  }

  private shouldLog(level: LogLevel, forceLog?: boolean): boolean {
    if (forceLog) return true;
    if (!isDevelopment) return level === 'error'; // Only errors in production
    return true;
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage(message), ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(message), ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(message), ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog('error', true)) {
      console.error(this.formatMessage(message), ...args);
    }
  }

  /**
   * Create a child logger with a specific prefix
   */
  child(prefix: string): Logger {
    const combinedPrefix = this.prefix 
      ? `${this.prefix.slice(1, -1)}:${prefix}` 
      : prefix;
    return new Logger(combinedPrefix);
  }

  /**
   * Group related logs together (dev only)
   */
  group(label: string, fn: () => void): void {
    if (!isDevelopment) return;
    console.group(this.formatMessage(label));
    fn();
    console.groupEnd();
  }

  /**
   * Time an operation (dev only)
   */
  time<T>(label: string, fn: () => T): T {
    if (!isDevelopment) return fn();
    console.time(this.formatMessage(label));
    const result = fn();
    console.timeEnd(this.formatMessage(label));
    return result;
  }

  /**
   * Log a table (dev only)
   */
  table(data: any): void {
    if (!isDevelopment) return;
    if (this.prefix) console.log(this.prefix);
    console.table(data);
  }
}

// Default logger instance
export const logger = new Logger();

// Pre-configured loggers for common use cases
export const authLogger = new Logger('Auth');
export const assessmentLogger = new Logger('Assessment');
export const dashboardLogger = new Logger('Dashboard');
export const subscriptionLogger = new Logger('Subscription');
export const companyLogger = new Logger('Company');
export const analyticsLogger = new Logger('Analytics');
export const sessionLogger = new Logger('Session');
export const translationLogger = new Logger('Translation');

// Factory function for creating module-specific loggers
export function createLogger(prefix: string): Logger {
  return new Logger(prefix);
}

export default logger;
